import {
  Checkbox,
  CheckboxGroup,
  GrandScore,
  Input,
  OdinCalendar,
  OptionBuilder,
  RadioGroup,
  RichTextArea,
  Signature,
  TextArea,
  TwinColumnSelect,
} from '@myosh/odin-components';
import { type RefObject, useEffect, useState } from 'react';
import type { LocaleItem } from '../../@types/common';
import type {
  CombinedFieldType,
  DynamicFormSettings,
  DynamicFormSettingsWithOptionalId,
  ExtendedDynamicFormField,
  OptionWithOptionalId,
} from '../../@types/forms';
import OdinDropDown from '../fields/custom-drop-downs/odin-drop-down.component';
import Questionnaire from '../fields/questionnaire/questionnaire.component';
import RiskRating from '../fields/risk-rating/risk-rating.component';
import UserRecordLink from '../fields/user-record-link/user-record-link.component';
import FormBuilderButtonField from './form-builder-button.component';
import FormBuilderLabelField from './form-builder-label.component';

export interface FormFieldMap {
  [key: string]: {
    text: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: React.FC<any>;
  };
}

export const formField: FormFieldMap = {
  CHECKBOX: { text: 'CHECKBOX', Component: Checkbox },
  CLONERECORD: { text: 'CLONERECORD', Component: FormBuilderButtonField },
  COMBOBOX: { text: 'COMBOBOX', Component: OdinDropDown },
  DATEFIELD: { text: 'DATEFIELD', Component: OdinCalendar },
  DOUBLEFIELD: { text: 'DOUBLEFIELD', Component: Input },
  EMAIL_THIS_BUTTON: { text: 'EMAIL_THIS_BUTTON', Component: FormBuilderButtonField },
  GEOLOCATION: { text: 'GEOLOCATION', Component: FormBuilderLabelField },
  GRAND_SCORE: { text: 'GRAND_SCORE', Component: GrandScore },
  HISTORY: { text: 'HISTORY', Component: RichTextArea },
  INTFIELD: { text: 'INTFIELD', Component: Input },
  INVITE_BUTTON: { text: 'INVITE_BUTTON', Component: FormBuilderButtonField },
  LABEL: { text: 'LABEL', Component: FormBuilderLabelField },
  LINK: { text: 'LINK', Component: Input },
  MULTISELECTCHECKBOX: { text: 'MULTISELECTCHECKBOX', Component: CheckboxGroup },
  MULTISELECTFIELD: { text: 'MULTISELECTFIELD', Component: OdinDropDown },
  MULTISELECTPERSONFIELD: { text: 'MULTISELECTPERSONFIELD', Component: OdinDropDown },
  NUMERIC_QUESTIONNAIRE: { text: 'NUMERIC_QUESTIONNAIRE', Component: Questionnaire },
  OPTION_BUILDER: { text: 'OPTION_BUILDER', Component: OptionBuilder },
  OPTIONGROUP: { text: 'OPTIONGROUP', Component: RadioGroup },
  PDF_EXPORT: { text: 'PDF_EXPORT', Component: FormBuilderButtonField },
  PERSONFIELD: { text: 'PERSONFIELD', Component: OdinDropDown },
  READERS: { text: 'READERS', Component: OdinDropDown },
  RECORDLINK: { text: 'RECORDLINK', Component: FormBuilderButtonField },
  REVERSE_RECORD_LINK: { text: 'REVERSE_RECORD_LINK', Component: FormBuilderLabelField },
  RICHTEXTAREA: { text: 'RICHTEXTAREA', Component: RichTextArea },
  RISKRATING: { text: 'RISKRATING', Component: RiskRating },
  SIGNATURECAPTURE: { text: 'SIGNATURECAPTURE', Component: Signature },
  SUMMARY_RECORD_LINK: { text: 'SUMMARY_RECORD_LINK', Component: FormBuilderLabelField },
  TEXTAREA: { text: 'TEXTAREA', Component: TextArea },
  TEXTFIELD: { text: 'TEXTFIELD', Component: Input },
  TEXTUAL_QUESTIONNAIRE: { text: 'TEXTUAL_QUESTIONNAIRE', Component: Questionnaire },
  TIMEFIELD: { text: 'TIMEFIELD', Component: OdinCalendar },
  TRAINING: { text: 'TRAINING', Component: OdinDropDown },
  TWINCOLUMNSELECT: { text: 'TWINCOLUMNSELECT', Component: TwinColumnSelect },
  TWINCOLUMNSELECTPERSONFIELD: { text: 'TWINCOLUMNSELECTPERSONFIELD', Component: TwinColumnSelect },
  USER_RECORDLINK: { text: 'USER_RECORDLINK', Component: UserRecordLink },
  WORD_EXPORT: { text: 'WORD_EXPORT', Component: FormBuilderButtonField },
  AI_FIELD: { text: 'AI_FIELD', Component: FormBuilderButtonField },
};

/**
 * Extracts the translation value from a caption object
 */
const extractCaptionText = (caption: unknown): string => {
  return (caption as { translations?: { value: string }[] })?.translations?.[0]?.value || '';
};

export function extendFieldProps(field: ExtendedDynamicFormField): Record<string, unknown> {
  const fieldType = field.fieldType as keyof FormFieldMap;

  if (fieldType === 'TWINCOLUMNSELECT' || fieldType === 'TWINCOLUMNSELECTPERSONFIELD') {
    return { allowSearch: false, data: [], valueField: 'id', textField: 'caption' };
  }

  if (fieldType === 'TIMEFIELD') {
    return {
      showTime: true,
      timeOnly: true,
      placeholder: 'HH:MM',
    };
  }

  if (fieldType === 'DATEFIELD') {
    return {
      showTime: true,
      placeholder: 'dd/MM/yyyy',
    };
  }

  if (fieldType === 'OPTIONGROUP' || fieldType === 'MULTISELECTCHECKBOX') {
    let additionalProperties: Record<string, unknown> = {
      valueField: 'id',
      textField: 'caption',
    };

    if (fieldType === 'MULTISELECTCHECKBOX' && field.options) {
      additionalProperties = {
        ...additionalProperties,
        data: field.options.map((option) => {
          return {
            ['id']: option.id,
            ['caption']: option?.caption?.translations?.[0]?.value ?? '',
          };
        }),
      };
    }

    return additionalProperties;
  }

  if (fieldType === 'OPTION_BUILDER') {
    return { allowSearch: false };
  }

  if (fieldType === 'TEXTUAL_QUESTIONNAIRE') {
    const clonedQuestions = structuredClone(field.questions) ?? [];
    const clonedAnswers = structuredClone(field.answers) ?? [];
    const sortedQuestions = clonedQuestions.sort((a, b) => a.position - b.position);
    const sortedAnswers = clonedAnswers.sort((a, b) => a.position - b.position);
    return {
      configuration: {
        id: field.id,
        showFieldCaption: true,
        questions: sortedQuestions
          ? sortedQuestions?.map((item) => ({
              ...item,
              id: item.id,
              caption: extractCaptionText(item.caption),
            }))
          : [],
        answers: sortedAnswers
          ? sortedAnswers?.map((item) => ({
              ...item,
              id: item.id,
              caption: extractCaptionText(item.caption),
            }))
          : [],
        type: 'TEXTUAL_QUESTIONNAIRE',
      },
    };
  }

  return {
    buttonCaption: field?.buttonCaption?.translations?.[0]?.value,
  };
}

export const prepareExistingFormForSubmission = (config: DynamicFormSettings): DynamicFormSettingsWithOptionalId => {
  const form = structuredClone(config) as DynamicFormSettingsWithOptionalId;

  form.fieldGroups = form.fieldGroups.map((group) => {
    group.fields = group.fields?.map((field) => {
      if ((field.fieldType as CombinedFieldType) === 'TEXTUAL_QUESTIONNAIRE') {
        field.questions = field.questions?.map((item) => {
          // removes id if it's a new/added item
          if (String(item.id).includes('newItem')) {
            delete item.id;
          }
          return item;
        });
        field.answers = field.answers?.map((item) => {
          // removes id if it's a new/added item
          if (String(item.id).includes('newItem')) {
            delete item.id;
          }
          return item;
        });
      }

      return field;
    });
    return group;
  });

  return form;
};

export const prepareImportedFormForSubmission = (config: DynamicFormSettings): DynamicFormSettingsWithOptionalId => {
  const form = structuredClone(config) as DynamicFormSettingsWithOptionalId;

  // Remove IDs from form, field groups and fields
  delete form.id;
  form.fieldGroups = form.fieldGroups.map((group) => {
    delete group.id;

    group.fields = group.fields.map((field) => {
      delete field.id;

      if ('groupId' in field) {
        delete field.groupId;
      }

      // The AI service generates 'options' with IDs, we need to remove them
      if ('options' in field && Array.isArray(field.options)) {
        field.options = field.options.map((option) => {
          const typedOption = option as OptionWithOptionalId;
          delete typedOption.id;
          return typedOption as typeof option;
        });
      }
      if ((field.fieldType as CombinedFieldType) === 'TEXTUAL_QUESTIONNAIRE') {
        field.questions = field.questions?.map((item) => {
          delete item.id;
          return item;
        });
        field.answers = field.answers?.map((item) => {
          delete item.id;
          return item;
        });
      }
      return field;
    });
    return group;
  });

  return form;
};

/**
 * Creates translation entries for the provided locales.
 *
 * Given a string value and a list of locale items, this helper returns a new array
 * of translation objects in the shape: { localeId, value } for each locale. It does
 * not attempt to read, merge or deduplicate against any existing caption data – the
 * caller is responsible for handling merges if required.
 *
 * @param value - The translation value to assign to every locale.
 * @param locales - The locales for which translation objects should be generated.
 * @returns A new array of translation objects (one per locale).
 */
export const createTranslations = (value: string, locales: LocaleItem[]) => {
  return locales.map((locale) => ({
    localeId: locale.id,
    value,
  }));
};

/**
 * Determines if the delete button should be offset.
 *
 * Rules:
 * 1. If the field has a label and visible input → offset relative to input
 * 2. If field has no label → center on input/button
 * 3. If field has only a visible label, input is hidden → center on label
 */
export function useFieldDeleteCentered(
  fieldRef: RefObject<HTMLElement | null>,
  inputSelector = 'input, textarea, select, button, .p-component',
  labelSelector = 'label'
): boolean {
  const [centerDelete, setCenterDelete] = useState(false);

  useEffect(() => {
    const container = fieldRef.current;
    if (!container) return;

    const update = () => {
      const labelEl = container.querySelector<HTMLElement>(labelSelector);
      const inputEl = container.querySelector<HTMLElement>(inputSelector);

      if (!labelEl && !inputEl) {
        setCenterDelete(false);
        return;
      }

      if (container) {
        const containerHeight = container.offsetHeight;

        if (containerHeight <= 20) {
          setCenterDelete(false);
        } else if (labelEl) {
          setCenterDelete(true);
        } else {
          setCenterDelete(false);
        }
      } else if (labelEl) {
        setCenterDelete(true);
      } else {
        setCenterDelete(false);
      }
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(container);

    return () => ro.disconnect();
  }, [fieldRef, inputSelector, labelSelector]);

  return centerDelete;
}
